import { motion } from "framer-motion";
import { HomeLayout } from "layouts";
import { useEffect, useRef, useState } from "react";
import * as cx from "classnames/bind";
import { useGQLSWR, fetcher, imageUrl, translate } from "utils";
import { gql } from "graphql-request";

import { useRouter } from "next/router";
import { cocktailFragmentWithOutMeta as cocktailFragment } from "queries/fragments";
import { Cocktail as CocktailComponent } from "components";
import { cocktailTransformer } from "queries/transformers";

import { Section, Container, CreateListModal } from "components";

import s from "styles/Journal.module.scss";
import g from "styles/Gallery.module.scss";

import data from "/public/postdata.json";
import Link from "next/link";
import Image from "next/legacy/image";
import Head from "next/head";
import urlSlug from "url-slug";
import { useTranslation } from "next-i18next";

const ArticleEmbed = ({ embedCode }) => {
  const embedRef = useRef(null);

  useEffect(() => {
    console.log("Received embedCode in ArticleEmbed:", embedCode);
    if (!embedCode) {
      console.log("No embedCode found");
      return;
    }

    if (!embedRef.current) {
      console.log("embedRef is not set yet");
      return;
    }

    console.log("Injecting embedCode:", embedCode);
    embedRef.current.innerHTML = embedCode;

    // Extract and execute script tags manually
    const scripts = embedRef.current.getElementsByTagName("script");

    for (let oldScript of scripts) {
      if (oldScript.src) {
        console.log("Injecting external script:", oldScript.src);
        const newScript = document.createElement("script");
        newScript.src = oldScript.src;
        newScript.async = oldScript.async;
        newScript.type = oldScript.type || "text/javascript";
        document.body.appendChild(newScript);
      } else if (oldScript.innerText) {
        console.log("Executing inline script...");
        try {
          eval(oldScript.innerText); // Execute inline script (use with caution)
        } catch (error) {
          console.error("Error executing inline script:", error);
        }
      }
    }
  }, [embedCode]);

  return <div ref={embedRef} className="TEST" />;
};

export const ArticlePage = ({
  article,
  category,
  setShowLoginModal,
  setLoginModalState,
  ...props
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const regex = /(<([^>]+)>)/gi;

  const [promotedCocktails, setPromotedCocktails] = useState([]);
  const [showCreateListModal, setShowCreateListModal] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const promotedQuery = `query articleRelated @cached(ttl: 300) {
            cocktails(where: {id: {_in: [${articleIds.join(",")}]}}) {
                ${cocktailFragment}
            }}`;
      const response = await fetcher(promotedQuery);
      setPromotedCocktails((response.cocktails || []).map(cocktailTransformer));
    }
    setPromotedCocktails([]);
    const articleIds =
      ((article || {}).extra_data || {}).relatedCocktails || [];
    if (articleIds.length > 0) {
      fetchData();
    }
  }, [article]);

  const extractCocktails = (obj, cocktails = []) => {
    if (obj) {
      cocktails = cocktails.concat(obj.cocktails || []);
      cocktails = extractCocktails(obj.parent, cocktails);
    }
    return cocktails;
  };

  useEffect(() => {
    if (window) {
      window.addEventListener("showLoginModal", (event) => {
        setLoginModalState({ index: 0, show: true });
      });
    }
  }, []);

  function renderSafeHTML(html) {
    return { __html: html };
  }

  return (
    <>
      <Head>
        <title key="title">
          {translate(article, "title", locale)} - Cocktail Club Journal
        </title>
        {article.excerpt.length && (
          <meta
            key="description"
            name="description"
            content={article.excerpt.replace(regex, "")}
          />
        )}
        <meta
          key="ogUrl"
          property="og:url"
          content={
            "https://cocktailclub.com/journal/" +
            category.slug +
            "/" +
            article.slug
          }
        />
        <link
          key="canonical"
          rel="canonical"
          href={
            "https://cocktailclub.com/journal/" +
            category.slug +
            "/" +
            article.slug
          }
        />

        <meta key="ogType" property="og:type" content="article" />
        <meta
          key="ogTitle"
          property="og:title"
          content={`${article.title} - Cocktail Club Journal `}
        />
        {article.excerpt.length && (
          <meta
            key="ogDescription"
            property="og:description"
            content={article.excerpt.replace(regex, "")}
          />
        )}
        <meta
          key="ogImage"
          property="og:image"
          content={imageUrl(article.image_id || "")}
        />
        <meta
          property="og:image:alt"
          content={`${article.title} - Cocktail Club Journal `}
        />

        <meta property="article:section" content={article.Categories} />
        <meta
          property="article:tag"
          content={`${article.title} - Cocktail Club Journal `}
        />
      </Head>
      <nav className={cx(s.Breadcrumbs)}>
        <Container>
          <span>
            <Link href="/journal">{t("Journal")}</Link>
          </span>
          <span>
            <Link href={"/journal/" + category.slug}>
              <>{translate(category, "name", locale)}</>
            </Link>
          </span>
          <span>{translate(article, "title", locale)}</span>
        </Container>
      </nav>
      <article itemScope itemType="http://schema.org/Article">
        <Section className="p0">
          <div className="flex flex-wrap">
            <div className="split-item-50__d">
              <img
                src={imageUrl(article.image_id || "")}
                layout="intrinsic"
                height={1024}
                width={1024}
                objectFit="cover"
              />
            </div>
            <div
              className={cx(
                "split-item-50__d flex align-center",
                s.postHeaderInfo,
              )}
            >
              <div className={cx(s.contentInner)}>
                <p className="Heading__styled">
                  {article.categories.map((category, index) => (
                    <Link
                      href={"/journal/" + urlSlug(category.category.slug)}
                      key={category.id}
                    >
                      {translate(category.category, "name", locale)}
                      {index !== article.categories.length - 1 && ", "}
                    </Link>
                  ))}
                </p>
                <h1 className="Heading__main Brick Light">
                  {translate(article, "title", locale)}
                </h1>
                {article.excerpt.length && (
                  <h2 className="Heading__sub">
                    {translate(article, "excerpt", locale).replace(regex, "")}
                  </h2>
                )}
              </div>
            </div>
          </div>
        </Section>
        <Section>
          <Container>
            <div className="test">
              <br />
              <br />
              {article?.embed_code && (
                <ArticleEmbed embedCode={article.embed_code} />
              )}
            </div>
            <div
              className={cx(s.postContent)}
              dangerouslySetInnerHTML={renderSafeHTML(
                article.translations[locale].content ||
                  article.translations.en.content,
              )}
            />
          </Container>
        </Section>
        <Section>
          <Container>
            {promotedCocktails.length > 0 ? (
              <>
                <h2 className="Heading mb24">{t("Related cocktails")}</h2>
                <motion.div
                  className={cx("GalleryFeed row", g.GalleryFeed)}
                  layout
                >
                  {(promotedCocktails || []).map((cocktail) => (
                    <CocktailComponent
                      key={cocktail.id}
                      cocktail={cocktail}
                      setShowCreateListModal={setShowCreateListModal}
                      setShowLoginModal={props.setShowLoginModal}
                      initial={false}
                      layout={false}
                      {...props}
                    />
                  ))}
                </motion.div>
              </>
            ) : (
              <></>
            )}
          </Container>
        </Section>
        <CreateListModal
          onClose={() => setShowCreateListModal(false)}
          show={showCreateListModal}
        />
      </article>
    </>
  );
};
